import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

export type OutlookTrade = {
  caseId: string;
  grade: string;
  direction: "LONG" | "SHORT";
  entryTime: string;
  closedAt: string;
  entry: number;
  result: string;
  r: number;
};

export type OutlookSession = { session: "asia" | "london" | "ny"; o: number; h: number; l: number; c: number };
export type OutlookDay = { date: string; o: number; h: number; l: number; c: number; range: number; sessions: OutlookSession[] };
export type OutlookCatalyst = { title: string; date: string; forecast: string | null; previous: string | null };
export type OutlookKeyEvent = { title: string; date: string; actual: string | null; forecast: string | null; priceBefore: number; priceAfter60: number; move60: number; range60: number };
export type OutlookLevels = { high: number; low: number; open: number; close: number; mid: number };

export type OutlookContent = {
  trades: OutlookTrade[];
  totals: { trades: number; wins: number; losses: number; be: number; netR: number };
  retrospective: string;
  outlook: string;
  levels: string;
  riskNote: string;
  bullCase?: string;
  bearCase?: string;
  prior?: { start: string; end: string };
  days?: OutlookDay[];
  catalysts?: OutlookCatalyst[];
  keyEvent?: OutlookKeyEvent | null;
  strongestDay?: string | null;
  levelData?: OutlookLevels | null;
  analyst?: { model: string | null; error: string | null };
};

export type WeeklyOutlook = {
  slug: string;
  week_start: string;
  week_end: string;
  title: string;
  summary: string;
  regime: string;
  content: OutlookContent;
  status: string;
  published_at: string | null;
};

const COLS = "slug, week_start, week_end, title, summary, regime, content, status, published_at";

async function publicClient() {
  const { createClient } = await import("@supabase/supabase-js");
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  return createClient(process.env["SUPABASE_URL"]!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export const listPublishedOutlooks = createServerFn({ method: "GET" }).handler(async (): Promise<WeeklyOutlook[]> => {
  const sb = await publicClient();
  const { data, error } = await sb.from("weekly_outlooks").select(COLS).eq("status", "published").order("week_start", { ascending: false }).limit(104);
  if (error) {
    console.error("[weekly-outlook] list failed", error.message);
    return [];
  }
  return (data ?? []) as unknown as WeeklyOutlook[];
});

export const getPublishedOutlook = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data }): Promise<WeeklyOutlook | null> => {
    const sb = await publicClient();
    const { data: row } = await sb.from("weekly_outlooks").select(COLS).eq("slug", data.slug).eq("status", "published").maybeSingle();
    return (row as unknown as WeeklyOutlook) ?? null;
  });

async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data } = await ctx.supabase.rpc("has_role", { _user_id: ctx.userId, _role: "admin" });
  if (!data) throw new Error("Forbidden");
}

export const listAllOutlooks = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<WeeklyOutlook[]> => {
    await assertAdmin(context);
    const { data } = await context.supabase.from("weekly_outlooks").select(COLS).order("week_start", { ascending: false }).limit(60);
    return (data ?? []) as unknown as WeeklyOutlook[];
  });

/** Month to date desk record for month end weeks (OTAS-01 closed trades plus the OSAS-01 swing case study). */
async function buildMonthReview(sb: any, priorEnd: string) {
  const end = new Date(`${priorEnd}T00:00:00Z`);
  const nextMon = new Date(end.getTime() + 3 * 86400000);
  if (nextMon.getUTCMonth() === end.getUTCMonth()) return null;
  const monthStart = `${priorEnd.slice(0, 7)}-01`;
  const { data } = await sb.from("otas_trades").select("grade, direction, entry_time, status, state").not("closed_at", "is", null).gte("entry_time", `${monthStart}T00:00:00Z`).lte("entry_time", `${priorEnd}T23:59:59Z`).order("entry_time");
  const intraday = (data ?? []).map((t: any) => {
    const usd = ((t.state?.legs ?? []) as any[]).reduce((s, l) => s + Number(l.exitMoveUsdPerOz ?? 0) * Number(l.quantityFractionOfOriginal ?? 0), 0);
    return { date: String(t.entry_time).slice(0, 10), grade: t.grade === "A_PLUS" ? "A+" : t.grade, direction: t.direction === 1 ? "LONG" : "SHORT", result: String(t.status).replace("CLOSED_", ""), r: Math.round((usd / 20) * 100) / 100 };
  });
  const intradayNetR = Math.round(intraday.reduce((s: number, t: { r: number }) => s + t.r, 0) * 100) / 100;
  const swing = { engine: "swing", direction: "SHORT", entryDate: "2026-10-19", entry: 4141.78, stop: 4190.13, tp1: 4022.46, tp2: 3931.22, result: "TP1 then TP2, stop at breakeven after TP1", r: 2.94, usdPerOz: 210.56, caseStudy: "/education/gold-swing-trading-strategy-case-study" };
  const swingInMonth = swing.entryDate.startsWith(priorEnd.slice(0, 7));
  return {
    month: priorEnd.slice(0, 7),
    intraday, intradayNetR,
    swing: swingInMonth ? swing : null,
    totalNetR: Math.round((intradayNetR + (swingInMonth ? swing.r : 0)) * 100) / 100,
    note: "Choppy, mostly sideways month: intraday stops clustered in range conditions, while one patient swing short captured the month end liquidation.",
  };
}

function fmtDate(iso: string) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

export const generateWeeklyOutlookDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { weekWindows, buildMarketData, draftWithAnalyst } = await import("./weekly-outlook.server");
    const { prior, ahead } = weekWindows();
    const wk = prior;
    const slug = `gold-weekly-outlook-${ahead.start}`;
    const { data: existing } = await supabaseAdmin.from("weekly_outlooks").select("status").eq("slug", slug).maybeSingle();
    if (existing?.status === "published") throw new Error("This week is already published");
    // Unlocks once the prior Mon to Fri week has closed (Friday 21:00 UTC).
    if (Date.now() < Date.parse(`${prior.end}T21:00:00Z`)) throw new Error("Generation unlocks after the Friday close");

    // Week in review counts positions CLOSED during the prior week, from both
    // engines, so multi-day swing exits land in the week they settled.
    const { data: rows } = await supabaseAdmin
      .from("otas_trades")
      .select("trade_id, grade, direction, entry_quote, entry_time, closed_at, status, state")
      .gte("closed_at", `${wk.start}T00:00:00Z`)
      .lte("closed_at", `${wk.end}T23:59:59Z`)
      .order("closed_at", { ascending: true });

    const intradayTrades: OutlookTrade[] = (rows ?? []).map((t) => {
      const legs = (((t.state as Record<string, unknown>)?.legs ?? []) as { exitMoveUsdPerOz: number; quantityFractionOfOriginal: number }[]);
      const usd = legs.reduce((s, l) => s + Number(l.exitMoveUsdPerOz ?? 0) * Number(l.quantityFractionOfOriginal ?? 0), 0);
      return {
        caseId: `otas-${t.trade_id}`,
        grade: t.grade === "A_PLUS" ? "A+" : (t.grade ?? "A"),
        direction: t.direction === 1 ? "LONG" : "SHORT",
        entryTime: t.entry_time,
        closedAt: t.closed_at as string,
        entry: Number(t.entry_quote),
        result: String(t.status).replace("CLOSED_", "").replace(/_/g, " + "),
        r: Math.round((usd / 20) * 100) / 100,
      };
    });
    const { data: swingSnap } = await supabaseAdmin
      .from("otas_swing_shadow_snapshots").select("trades").order("created_at", { ascending: false }).limit(1).maybeSingle();
    type SwingT = { openedAt: number; closedAt: number; direction: "long" | "short"; entry: number; outcome: string; r: number };
    const wkFromS = Date.parse(`${wk.start}T00:00:00Z`) / 1000;
    const wkToS = Date.parse(`${wk.end}T23:59:59Z`) / 1000;
    const swingTrades: OutlookTrade[] = ((swingSnap?.trades ?? []) as SwingT[])
      .filter((t) => Number.isFinite(t.closedAt) && t.closedAt >= wkFromS && t.closedAt <= wkToS)
      .map((t) => ({
        caseId: `osas-${t.openedAt}`,
        grade: "A+",
        direction: t.direction === "long" ? "LONG" : "SHORT",
        entryTime: new Date(t.openedAt * 1000).toISOString(),
        closedAt: new Date(t.closedAt * 1000).toISOString(),
        entry: Math.round(Number(t.entry) * 100) / 100,
        result: t.outcome === "TP2" ? "TP1 + TP2" : t.outcome,
        r: Math.round(Number(t.r) * 100) / 100,
      }));
    const trades = [...intradayTrades, ...swingTrades].sort((a, b) => Date.parse(a.closedAt) - Date.parse(b.closedAt));
    const netR = Math.round(trades.reduce((s, t) => s + t.r, 0) * 100) / 100;
    const wins = trades.filter((t) => t.r > 0).length;
    const losses = trades.filter((t) => t.r < 0).length;
    const totals = { trades: trades.length, wins, losses, be: trades.length - wins - losses, netR };

    const market = await buildMarketData(supabaseAdmin, prior, ahead);
    const { draft, model, error: analystError } = await draftWithAnalyst({
      weekAhead: ahead,
      catalystsWeekAhead: market.catalysts,
      priorWeek: prior,
      priorWeekLevels: market.levelData,
      priorWeekDays: market.days.map((d) => ({ date: d.date, o: d.o, h: d.h, l: d.l, c: d.c, range: d.range, sessions: d.sessions })),
      priorWeekKeyRelease: market.keyEvent,
      priorWeekReleases: market.releases,
      cot: market.cot,
      strongestDay: market.strongestDay,
      deskOutcome: totals,
      monthReview: await buildMonthReview(supabaseAdmin, prior.end),
    });
    const fallback = "Edit before approval: analyst draft unavailable.";
    const content: OutlookContent = {
      trades,
      totals,
      retrospective: draft?.retrospective ?? (trades.length === 0
        ? "No positions met the acceptance criteria last week. Standing aside was the operational outcome."
        : `The desk closed ${trades.length} position${trades.length === 1 ? "" : "s"} last week for a net ${netR >= 0 ? "+" : ""}${netR}R.`),
      outlook: draft?.outlook ?? fallback,
      bullCase: draft?.bullCase ?? fallback,
      bearCase: draft?.bearCase ?? fallback,
      levels: draft?.levels ?? fallback,
      riskNote: draft?.riskNote ??
        "Liquidity is thin during the Sunday open. Institutional liquidity typically matures at the London open (07:00 UTC) and the London and New York overlap. Size positions to personal drawdown limits.",
      prior,
      days: market.days,
      catalysts: market.catalysts,
      keyEvent: market.keyEvent,
      strongestDay: market.strongestDay,
      levelData: market.levelData,
      analyst: { model, error: analystError },
    };
    const title = draft?.title ?? `Gold Weekly Outlook: ${fmtDate(ahead.start)} to ${fmtDate(ahead.end)}`;
    const summary = draft?.summary ?? `The liquidity map and macro catalysts for the week of ${fmtDate(ahead.start)}, with a short review of the prior week.`;
    const regime = draft?.regime ?? "range";

    const { error } = await supabaseAdmin.from("weekly_outlooks").upsert(
      { slug, week_start: ahead.start, week_end: ahead.end, title, summary, regime, content, status: "draft", updated_at: new Date().toISOString() },
      { onConflict: "slug" },
    );
    if (error) throw new Error(error.message);

    // Notify admins on Telegram with the preview link.
    try {
      const { sendTelegramMessage } = await import("@/lib/telegram.server");
      const { data: bindings } = await supabaseAdmin.from("telegram_bindings").select("user_id, chat_id").eq("status", "active").not("chat_id", "is", null);
      const html = [
        "<b>XAU PRO ACADEMY</b>",
        "<b>ADMIN DESK · WEEKLY OUTLOOK READY</b>",
        "",
        `<code>Week    ${ahead.start} to ${ahead.end}</code>`,
        `<code>Analyst ${draft ? "drafted" : "unavailable, edit manually"}</code>`,
        `<code>Prior   ${totals.trades} trades</code>`,
        `<code>Net     ${netR >= 0 ? "+" : ""}${netR}R</code>`,
        "",
        `Review and approve: https://xauproacademy.com/admin/weekly-outlook?slug=${slug}`,
      ].join("\n");
      for (const b of bindings ?? []) {
        const { data: isAdmin } = await supabaseAdmin.rpc("has_role", { _user_id: b.user_id, _role: "admin" });
        if (isAdmin) await sendTelegramMessage(Number(b.chat_id), html).catch(() => null);
      }
    } catch (e) {
      console.error("[weekly-outlook] telegram notify failed", e instanceof Error ? e.message : e);
    }
    return { slug };
  });

const saveSchema = z.object({
  slug: z.string().min(1).max(120),
  title: z.string().min(5).max(200),
  summary: z.string().max(400),
  regime: z.enum(["bull", "bear", "range"]),
  retrospective: z.string().max(6000),
  outlook: z.string().max(6000),
  levels: z.string().max(4000),
  riskNote: z.string().max(2000),
  bullCase: z.string().max(3000),
  bearCase: z.string().max(3000),
  catalysts: z
    .array(
      z.object({
        title: z.string().min(1).max(120),
        date: z.string().min(10).max(40),
        forecast: z.string().max(20).nullable(),
        previous: z.string().max(20).nullable(),
      }),
    )
    .max(30)
    .optional(),
});

export const saveOutlookDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: z.infer<typeof saveSchema>) => saveSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin.from("weekly_outlooks").select("content").eq("slug", data.slug).maybeSingle();
    if (!row) throw new Error("Draft not found");
    const prevContent = row.content as OutlookContent;
    const catalysts = data.catalysts
      ? data.catalysts.map((c) => ({ ...c, forecast: c.forecast?.trim() || null, previous: c.previous?.trim() || null }))
      : prevContent.catalysts;
    const content = { ...prevContent, catalysts, retrospective: data.retrospective, outlook: data.outlook, levels: data.levels, riskNote: data.riskNote, bullCase: data.bullCase, bearCase: data.bearCase };
    const { error } = await supabaseAdmin
      .from("weekly_outlooks")
      .update({ title: data.title, summary: data.summary, regime: data.regime, content, updated_at: new Date().toISOString() })
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const approveOutlook = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(120) }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("weekly_outlooks")
      .update({ status: "published", published_at: new Date().toISOString(), approval_token: null, updated_at: new Date().toISOString() })
      .eq("slug", data.slug);
    if (error) throw new Error(error.message);
    try {
      const { submitToIndexNow } = await import("@/lib/indexnow.functions");
      await submitToIndexNow([
        `https://xauproacademy.com/weekly-outlook/${data.slug}`,
        "https://xauproacademy.com/weekly-outlook",
      ]);
    } catch (e) {
      console.error("[weekly-outlook] indexnow ping failed", e);
    }
    return { ok: true, url: `/weekly-outlook/${data.slug}` };
  });
